import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Save, Plus } from "lucide-react";
import { SPECIAL_SECTIONS, errMsg, fieldCls } from "./adminShared";

function CategoryRow({ cat, index, count, onMove, onSaved, onDelete }) {
    const [name, setName] = useState(cat.name);
    const [description, setDescription] = useState(cat.description || "");
    const dirty = name !== cat.name || description !== (cat.description || "");
    const isSpecial = SPECIAL_SECTIONS.includes(cat.slug);

    const save = async () => {
        try {
            await client.patch(`/admin/categories/${cat.slug}`, { name, description });
            toast.success("Category saved");
            onSaved();
        } catch (err) {
            toast.error(errMsg(err, "Save failed"));
        }
    };

    return (
        <div data-testid={`category-row-${cat.slug}`} className="grid grid-cols-[56px_1fr] md:grid-cols-[56px_220px_1fr_auto] gap-3 items-center px-4 py-3 border-t border-brand-line">
            <div className="flex flex-col gap-1">
                <button disabled={index === 0} onClick={() => onMove(index, -1)} data-testid={`category-up-${cat.slug}`} className="p-1 border border-brand-line rounded-sm disabled:opacity-30 hover:border-brand-gold"><ArrowUp size={12} /></button>
                <button disabled={index === count - 1} onClick={() => onMove(index, 1)} data-testid={`category-down-${cat.slug}`} className="p-1 border border-brand-line rounded-sm disabled:opacity-30 hover:border-brand-gold"><ArrowDown size={12} /></button>
            </div>
            <input value={name} onChange={(e) => setName(e.target.value)} className={`${fieldCls} !mt-0`} data-testid={`category-name-${cat.slug}`} />
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description (optional)" className={`${fieldCls} !mt-0 col-span-2 md:col-span-1`} />
            <div className="flex gap-2 col-span-2 md:col-span-1 items-center">
                {isSpecial && <span className="text-[10px] text-brand-mute uppercase tracking-wider">Specials section</span>}
                <button disabled={!dirty} onClick={save} data-testid={`category-save-${cat.slug}`} className="p-2 border border-brand-line rounded-sm disabled:opacity-30 hover:border-brand-olive"><Save size={13} /></button>
                {!isSpecial && (
                    <button onClick={() => onDelete(cat)} data-testid={`category-delete-${cat.slug}`} className="p-2 border border-brand-line rounded-sm hover:border-destructive hover:text-destructive"><Trash2 size={13} /></button>
                )}
            </div>
        </div>
    );
}

export default function CategoriesPanel({ onChanged }) {
    const [cats, setCats] = useState([]);
    const [newName, setNewName] = useState("");

    const load = () => client.get("/categories").then((r) => setCats(r.data));
    useEffect(() => { load(); }, []);
    const refresh = () => { load(); if (onChanged) onChanged(); };

    const move = async (i, dir) => {
        const next = [...cats];
        [next[i], next[i + dir]] = [next[i + dir], next[i]];
        setCats(next);
        await client.post("/admin/categories/reorder", { ids: next.map((c) => c.slug) });
        refresh();
    };

    const add = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        try {
            await client.post("/admin/categories", { name: newName.trim() });
            toast.success("Category added");
            setNewName("");
            refresh();
        } catch (err) {
            toast.error(errMsg(err, "Could not add category"));
        }
    };

    const remove = async (cat) => {
        if (!window.confirm(`Delete category "${cat.name}"?`)) return;
        try {
            await client.delete(`/admin/categories/${cat.slug}`);
            toast.success("Category deleted");
            refresh();
        } catch (err) {
            toast.error(errMsg(err, "Delete failed"));
        }
    };

    return (
        <div className="space-y-6">
            <form onSubmit={add} className="flex flex-wrap gap-2 items-center">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name" className={`${fieldCls} !mt-0 max-w-xs`} data-testid="category-new-name" />
                <button className="btn-primary text-sm inline-flex items-center gap-2" data-testid="category-add-btn"><Plus size={14} /> Add category</button>
            </form>
            <p className="text-xs text-brand-mute">Order here is the order customers see on the Menu page. Categories with items can't be deleted.</p>
            <div className="border border-brand-line rounded-sm bg-brand-charcoal">
                {cats.map((c, i) => (
                    <CategoryRow key={c.slug} cat={c} index={i} count={cats.length} onMove={move} onSaved={refresh} onDelete={remove} />
                ))}
            </div>
        </div>
    );
}
